/**
 * Question set grading for ContentPlayer (engine core).
 */

import type { ContentGrader, GradingResult, ScoringConfig, QuestionCounter } from '@ai-content-hub/content-core';
import {
    mcqGrader,
    isMcqAnswered,
    matchGrader,
    fitbGrader,
    orderingGrader,
    type MCQAnswer,
    type MatchAnswer,
    type FITBAnswer,
    type OrderingAnswer,
} from '@ai-content-hub/content-elements';
import type { QuestionSetData, QuestionSetItem, QuestionSetScoringConfig } from './schema';
import map from 'lodash/map';
import trim from 'lodash/trim';

export interface QuestionSetItemResult {
    itemId: string;
    isCorrect: boolean;
    score: number;
    /** Unified scale minimum (ACH item: always 0). */
    min: number;
    /** Unified scale maximum for this item. */
    max: number;
    /** Sub-question grader details (e.g. FITB `perBlank`, MCQ hints). */
    details?: Record<string, unknown>;
}

function isMatchAnswered(item: QuestionSetItem, answer: unknown, allowPartial = false): boolean {
    if (item.type !== 'match') return false;
    if (answer == null || typeof answer !== 'object') return false;
    const m = answer as MatchAnswer;
    const matchItems = item.data.items ?? [];
    const filled = (it: { id: string }) => {
        const v = m[it.id];
        return typeof v === 'string' && trim(v) !== '';
    };
    return allowPartial ? matchItems.some(filled) : matchItems.every(filled);
}

function isFitbAnswered(item: QuestionSetItem, answer: unknown, allowPartial = false): boolean {
    if (item.type !== 'fitb-dropdown' && item.type !== 'fitb-type') return false;
    const blanks = item.data.blanks ?? [];
    if (blanks.length === 0) return !allowPartial;
    if (answer == null || typeof answer !== 'object') return false;
    const f = answer as FITBAnswer;
    const filled = (b: { id: string }) => {
        const v = f[b.id];
        return typeof v === 'string' && trim(v) !== '';
    };
    return allowPartial ? blanks.some(filled) : blanks.every(filled);
}

function isOrderingAnswered(item: QuestionSetItem, answer: unknown, allowPartial = false): boolean {
    if (item.type !== 'ordering') return false;
    if (!Array.isArray(answer)) return false;
    if (allowPartial) return answer.some((v) => typeof v === 'string' && trim(v) !== '');
    const orderItems = item.data.items ?? [];
    return answer.length === orderItems.length && answer.every((v) => typeof v === 'string' && trim(v) !== '');
}

function isItemAnswered(item: QuestionSetItem, raw: unknown, allowPartial = false): boolean {
    switch (item.type) {
        case 'mcq':
            return isMcqAnswered(raw);
        case 'match':
            return isMatchAnswered(item, raw, allowPartial);
        case 'fitb-dropdown':
        case 'fitb-type':
            return isFitbAnswered(item, raw, allowPartial);
        case 'ordering':
            return isOrderingAnswered(item, raw, allowPartial);
    }
}

export function validateQuestionSetAnswer(
    data: QuestionSetData,
    answer: unknown,
    opts?: { allowPartial?: boolean }
): boolean {
    if (!answer || typeof answer !== 'object' || Array.isArray(answer)) {
        return false;
    }
    const rec = answer as Record<string, unknown>;
    const items = data.items ?? [];
    if (items.length === 0) {
        return false;
    }
    return items.some((item) => isItemAnswered(item, rec[item.id], opts?.allowPartial === true));
}

export function gradeQuestionSet(
    data: QuestionSetData,
    answers: Record<string, unknown>,
    scoring?: QuestionSetScoringConfig & ScoringConfig
): GradingResult {
    const items = data.items ?? [];
    const scoringConfig = scoring as QuestionSetScoringConfig | undefined;
    const pointsBase =
        scoringConfig?.pointsPerQuestion ?? (scoring as { max?: number } | undefined)?.max ?? 1;
    const passingThreshold =
        scoringConfig?.passingThreshold ?? (scoring as { passingScore?: number } | undefined)?.passingScore ?? 0.7;
    const partialCredit = scoring?.partialCreditEnabled ?? true;

    const itemResults: QuestionSetItemResult[] = map(items, (item) => {
        const raw = answers[item.id];
        const itemMax = item.points ?? pointsBase;

        switch (item.type) {
            case 'mcq': {
                const gradeResult = mcqGrader.grade(item.data, raw as MCQAnswer, {
                    max: itemMax,
                    partialCredit,
                });
                return {
                    itemId: item.id,
                    isCorrect: gradeResult.isCorrect,
                    score: gradeResult.score,
                    min: 0,
                    max: gradeResult.max,
                    details: gradeResult.details,
                };
            }
            case 'match': {
                const gradeResult = matchGrader.grade(item.data, (raw as MatchAnswer) ?? {}, {
                    max: itemMax,
                    partialCredit,
                });
                return {
                    itemId: item.id,
                    isCorrect: gradeResult.isCorrect,
                    score: gradeResult.score,
                    min: 0,
                    max: gradeResult.max,
                    details: gradeResult.details,
                };
            }
            case 'fitb-dropdown':
            case 'fitb-type': {
                const gradeResult = fitbGrader.grade(item.data, (raw as FITBAnswer) ?? {}, {
                    max: itemMax,
                    partialCredit,
                });
                return {
                    itemId: item.id,
                    isCorrect: gradeResult.isCorrect,
                    score: gradeResult.score,
                    min: 0,
                    max: gradeResult.max,
                    details: gradeResult.details,
                };
            }
            case 'ordering': {
                const gradeResult = orderingGrader.grade(item.data, (raw as OrderingAnswer) ?? [], {
                    max: itemMax,
                    partialCredit,
                });
                return {
                    itemId: item.id,
                    isCorrect: gradeResult.isCorrect,
                    score: gradeResult.score,
                    min: 0,
                    max: gradeResult.max,
                    details: gradeResult.details,
                };
            }
        }
    });

    const totalScore = itemResults.reduce((sum, r) => sum + r.score, 0);
    const maxPoints = itemResults.reduce((sum, r) => sum + r.max, 0);
    const ratio = maxPoints > 0 ? totalScore / maxPoints : 0;
    const passed = ratio >= passingThreshold;

    const resultByItemId = new Map(map(itemResults, (r) => [r.itemId, r] as const));
    let childCorrect = 0;
    let childTotal = 0;
    for (const item of items) {
        const r = resultByItemId.get(item.id);
        switch (item.type) {
            case 'mcq': {
                childTotal += 1;
                if (r?.isCorrect === true) {
                    childCorrect += 1;
                }
                break;
            }
            case 'match': {
                const rows = item.data.items ?? [];
                const n = rows.length;
                childTotal += n;
                const d = r?.details as { itemResults?: Record<string, boolean> } | undefined;
                const ir = d?.itemResults;
                if (ir) {
                    for (const row of rows) {
                        if (ir[row.id] === true) {
                            childCorrect += 1;
                        }
                    }
                }
                break;
            }
            case 'fitb-dropdown':
            case 'fitb-type': {
                const blanks = item.data.blanks ?? [];
                const n = blanks.length;
                childTotal += n;
                const d = r?.details as { perBlank?: { id: string; ok: boolean }[] } | undefined;
                const pb = d?.perBlank;
                if (Array.isArray(pb)) {
                    for (const p of pb) {
                        if (p.ok === true) {
                            childCorrect += 1;
                        }
                    }
                }
                break;
            }
            case 'ordering': {
                const orderItems = item.data.items ?? [];
                const n = orderItems.length;
                childTotal += n;
                const d = r?.details as { correctPositions?: number } | undefined;
                if (d?.correctPositions != null) {
                    childCorrect += d.correctPositions;
                }
                break;
            }
        }
    }

    const questionCorrectCount = itemResults.filter((r) => r.isCorrect).length;

    return {
        isCorrect: passed,
        score: totalScore,
        max: maxPoints,
        details: {
            items: itemResults,
            passed,
            ratio,
            correctCount: childCorrect,
            totalItems: childTotal,
            questionCorrectCount,
            questionCount: itemResults.length,
        },
    };
}
/**
 * Count the total number of gradable answer units in a question set.
 *
 * Mirrors the childTotal logic in gradeQuestionSet:
 *   mcq      → 1
 *   match    → number of match items (pairs)
 *   fitb-*   → number of blanks
 *   ordering → number of ordering items
 */
export function countQuestionSetQuestions(data: QuestionSetData): number {
    const items = data.items ?? [];
    let count = 0;
    for (const item of items) {
        switch (item.type) {
            case 'mcq':
                count += 1;
                break;
            case 'match':
                count += (item.data.items ?? []).length;
                break;
            case 'fitb-dropdown':
            case 'fitb-type':
                count += (item.data.blanks ?? []).length;
                break;
            case 'ordering':
                count += (item.data.items ?? []).length;
                break;
        }
    }
    return count;
}

export const questionSetContentGrader: ContentGrader<QuestionSetData, Record<string, unknown>> = {
    grade: (data, answer, cfg) => gradeQuestionSet(data, answer ?? {}, cfg as QuestionSetScoringConfig),
    validateAnswer: (answer, data, opts) => validateQuestionSetAnswer(data, answer, opts),
};

export const questionSetQuestionCounter: QuestionCounter<QuestionSetData> = {
    countQuestions: countQuestionSetQuestions,
};
